import { fromEvent, merge, Subject, Subscription, timer, interval } from 'rxjs';
import { debounceTime, switchMap } from 'rxjs/operators';

export class IdleTimeoutService {
  private static timeoutMs = 60 * 60 * 1000; // 1 hour
  private static warningTimeoutMs = 59 * 60 * 1000; // 59 minutes (warning before logout)
  private static debounceMs = 300;
  private static attachDom = true;

  private static initialized = false;
  private static active = true;
  private static lastResetAt = 0;
  private static warningEmitted = false;
  private static timeoutEmitted = false;

  private static reset$ = new Subject<void>();
  private static _onTimeout$ = new Subject<void>();
  private static _onWarningTimeout$ = new Subject<void>();

  private static idleSub?: Subscription;
  private static warningSub?: Subscription;
  private static idleRemainingSub?: Subscription;
  private static warningRemainingSub?: Subscription;
  private static domEventsSub?: Subscription;
  private static heartbeatSub?: Subscription;

  static readonly onTimeout$ = IdleTimeoutService._onTimeout$.asObservable();
  static readonly onWarningTimeout$ = IdleTimeoutService._onWarningTimeout$.asObservable();

  static init(opts?: {
    timeoutMs?: number;
    warningTimeoutMs?: number;
    debounceMs?: number;
    attachDomListeners?: boolean;
  }): void {
    if (opts?.timeoutMs != null) this.timeoutMs = opts.timeoutMs;
    if (opts?.warningTimeoutMs != null) this.warningTimeoutMs = opts.warningTimeoutMs;
    else if (opts?.timeoutMs != null) {
      // Default warning timeout to 1 minute before logout
      this.warningTimeoutMs = opts.timeoutMs - 60 * 1000;
    }
    if (opts?.debounceMs != null) this.debounceMs = opts.debounceMs;
    if (opts?.attachDomListeners != null) this.attachDom = opts.attachDomListeners;

    // Idempotent: already watching — keep elapsed clock; do not re-arm or reset.
    if (this.initialized) {
      return;
    }

    if (this.attachDom) this.wireDomActivityResets();
    else this.unwireDomActivityResets();

    this.startIdleWatch();
    this.startWarningWatch();
    this.startHeartbeat();
    this.initialized = true;
    this.setActive(true);
  }

  static reset(): void {
    if (!this.initialized || !this.active) return;

    // Wall-clock guard: waking the device often delivers a touch before appStateChange.
    // Do not wipe elapsed idle time if the session has already timed out.
    if (this.lastResetAt && this.getElapsedMs() >= this.timeoutMs) {
      this.emitTimeout();
      return;
    }

    this.clearRemainingWatches();
    this.warningEmitted = false;
    this.timeoutEmitted = false;
    this.lastResetAt = Date.now();
    // This will reset both idle and warning timers since they both subscribe to reset$
    this.reset$.next();
  }

  /** True when reset() ran after the given timestamp (e.g. treatment idle handler saved and returned to welcome). */
  static wasResetSince(timestampMs: number): boolean {
    return this.lastResetAt > timestampMs;
  }

  static getElapsedMs(): number {
    if (!this.lastResetAt) return 0;
    return Date.now() - this.lastResetAt;
  }

  /**
   * Wall-clock check after the app returns to foreground. JS timers freeze while
   * backgrounded; this fires warning/timeout based on real elapsed time, or
   * restarts watches with remaining durations.
   */
  static handleAppResume(): void {
    if (!this.initialized || !this.active) return;

    const elapsed = this.getElapsedMs();
    if (elapsed <= 0) {
      this.reset();
      return;
    }

    if (elapsed >= this.timeoutMs) {
      this.emitTimeout();
      return;
    }

    // Drop frozen pre-background timers; keep lastResetAt so remaining math stays correct.
    this.rearmIdleWatch();
    this.rearmWarningWatch();

    if (elapsed >= this.warningTimeoutMs) {
      this.emitWarning();
      this.scheduleIdleRemaining(this.timeoutMs - elapsed);
      return;
    }

    this.scheduleWarningRemaining(this.warningTimeoutMs - elapsed);
    this.scheduleIdleRemaining(this.timeoutMs - elapsed);
  }

  static setActive(enabled: boolean): void {
    this.active = enabled;
    if (enabled) this.reset();
  }

  static setTimeoutMs(ms: number): void {
    this.timeoutMs = ms;
    if (this.initialized) {
      this.startIdleWatch();
      this.startWarningWatch();
    }
  }

  /**
   * Manually triggers the timeout event and waits for all async handlers to complete.
   * Since we can't directly track when observable subscribers finish their async operations,
   * we trigger the timeout and wait with a configurable maximum timeout.
   * This ensures handlers like completeTreatment have time to finish even with slow network calls.
   *
   * @param maxWaitMs Maximum time to wait for handlers to complete (default: 30 seconds)
   * @returns Promise that resolves after triggering timeout and waiting up to maxWaitMs
   */
  static async triggerTimeoutAndWait(maxWaitMs: number = 30000): Promise<void> {
    if (!this.initialized) {
      console.warn('IdleTimeoutService not initialized, cannot trigger timeout');
      return;
    }

    // Trigger the timeout observable - this will cause all subscribers to execute
    this.emitTimeout();

    // Since we can't directly track when observable subscribers complete their async operations,
    // we wait a reasonable amount of time for handlers to finish.
    // The maxWaitMs parameter allows callers to adjust based on expected operation duration.
    // For network operations like saving treatment data, 30 seconds should be sufficient.

    // Give handlers a small initial delay to start their async operations
    await new Promise(resolve => setTimeout(resolve, 100));

    // Now wait the remaining time, allowing handlers to complete
    // We'll log progress to help with debugging
    const startTime = Date.now();
    const checkInterval = 1000; // Check every second
    let elapsed = 0;

    while (elapsed < maxWaitMs) {
      await new Promise(resolve => setTimeout(resolve, Math.min(checkInterval, maxWaitMs - elapsed)));
      elapsed = Date.now() - startTime;

      // Log progress every 5 seconds
      if (elapsed % 5000 < checkInterval) {
      }
    }
  }

  static destroy(): void {
    this.idleSub?.unsubscribe();
    this.warningSub?.unsubscribe();
    this.clearRemainingWatches();
    this.domEventsSub?.unsubscribe();
    this.heartbeatSub?.unsubscribe();
    this.initialized = false;
  }

  // ---------------- private ----------------

  private static emitTimeout(): void {
    if (!this.active || this.timeoutEmitted) return;
    this.timeoutEmitted = true;
    this._onTimeout$.next();
  }

  private static emitWarning(): void {
    if (!this.active || this.warningEmitted || this.timeoutEmitted) return;
    this.warningEmitted = true;
    this._onWarningTimeout$.next();
  }

  private static startIdleWatch(): void {
    this.rearmIdleWatch();
    this.reset();
  }

  private static startWarningWatch(): void {
    this.rearmWarningWatch();
    this.reset();
  }

  /** Subscribe to reset$ for full-duration timers without touching lastResetAt. */
  private static rearmIdleWatch(): void {
    this.idleSub?.unsubscribe();
    this.idleSub = this.reset$
      .pipe(switchMap(() => timer(this.timeoutMs)))
      .subscribe(() => {
        this.emitTimeout();
      });
  }

  private static rearmWarningWatch(): void {
    this.warningSub?.unsubscribe();
    this.warningSub = this.reset$
      .pipe(switchMap(() => timer(this.warningTimeoutMs)))
      .subscribe(() => {
        this.emitWarning();
      });
  }

  private static scheduleIdleRemaining(remainingMs: number): void {
    this.idleRemainingSub?.unsubscribe();
    const delayMs = Math.max(0, remainingMs);
    this.idleRemainingSub = timer(delayMs).subscribe(() => {
      this.idleRemainingSub = undefined;
      if (!this.active || !this.initialized) return;
      if (this.getElapsedMs() >= this.timeoutMs) {
        this.emitTimeout();
      }
    });
  }

  private static scheduleWarningRemaining(remainingMs: number): void {
    this.warningRemainingSub?.unsubscribe();
    const delayMs = Math.max(0, remainingMs);
    this.warningRemainingSub = timer(delayMs).subscribe(() => {
      this.warningRemainingSub = undefined;
      if (!this.active || !this.initialized) return;
      const elapsed = this.getElapsedMs();
      if (elapsed >= this.warningTimeoutMs && elapsed < this.timeoutMs) {
        this.emitWarning();
      }
    });
  }

  private static clearRemainingWatches(): void {
    this.idleRemainingSub?.unsubscribe();
    this.idleRemainingSub = undefined;
    this.warningRemainingSub?.unsubscribe();
    this.warningRemainingSub = undefined;
  }

  /**
   * Wall-clock backup for long RxJS timers that may be delayed by WebView throttling.
   * Checks elapsed idle time every 5s and emits warning/timeout when thresholds are met.
   */
  private static startHeartbeat(): void {
    this.heartbeatSub?.unsubscribe();
    this.heartbeatSub = interval(5000).subscribe(() => {
      if (!this.initialized || !this.active || !this.lastResetAt) return;

      const elapsed = this.getElapsedMs();
      if (elapsed >= this.timeoutMs) {
        this.emitTimeout();
        return;
      }
      if (elapsed >= this.warningTimeoutMs) {
        this.emitWarning();
      }
    });
  }

  private static wireDomActivityResets(): void {
    this.domEventsSub?.unsubscribe();
    const opts: AddEventListenerOptions = { capture: true, passive: true };

    // Intentional input only — scroll/wheel/move/visibility/focus must not
    // keep the session alive or wipe elapsed time on app resume.
    const activity$ = merge(
      fromEvent(document, 'pointerdown', opts),
      fromEvent(document, 'pointerup', opts),
      fromEvent(document, 'touchstart', opts),
      fromEvent(document, 'touchend', opts),
      fromEvent(document, 'mousedown', opts),
      fromEvent(document, 'mouseup', opts),
      fromEvent(document, 'keydown', { capture: true }),
    ).pipe(debounceTime(this.debounceMs));

    this.domEventsSub = activity$.subscribe(() => {
      this.reset();
    });
  }

  private static unwireDomActivityResets(): void {
    this.domEventsSub?.unsubscribe();
    this.domEventsSub = undefined;
  }
}
